import Vuex from 'vuex';
import { WoliService } from '@/scripts/services/woli/woli-service';
import { helpers } from '@/scripts/helpers';
import moment from 'moment';
import store from '@/stores';
import { api } from '@/scripts/api';

export const WOLI_POPUP_TYPE = {
    DEFAULT: 'DEFAULT',
    CONTEXT_SPECIFIC: 'CONTEXT_SPECIFIC',
};

export const createWoliPopupStore = () => new Vuex.Store({
    state: {
        isLoadingConversations: false,
        isLoadingConversation: false,
        isLoadingRename: false,
        isAssistantResponding: false,
        isAssistantTyping: false,

        conversations: [],
        conversationId: null,
        messages: null,
        assistantResponseShown: [],
        userInput: null,
        context: {
            type: null,
            variables: {},
        },
        pendingMessage: null,
        respondingMessageIndex: null,

        renameConversationId: null,
        renameConversationName: '',

        conversationContainerElement: null,
        userInputElement: null,

        limitReachedMessage: null,

        regeneratedConversationId: null,

        loadConversationAbortController: new AbortController(),

        popupType: WOLI_POPUP_TYPE.DEFAULT,
    },

    getters: {
        accountId() {
            return store.state.context.account.id;
        },

        selectedConversation(state) {
            return state.conversationId ? state.conversations.find((conversation) => conversation.id === state.conversationId): null;
        },

        renamingConversation(state) {
            return state.conversations.find((conversation) => conversation.id === state.renameConversationId);
        },

        messages(state) {
            if (!state.messages) {
                return null;
            }

            let assistantIndex = 0;

            return state.messages.map((message) => {
                return {
                    role: message.role,
                    content: message.content,
                    assistantResponseIndex: message.role === 'assistant' ? assistantIndex++ : null,
                };
            });
        },

        assistantResponseShown(state) {
            return state.assistantResponseShown;
        }
    },

    mutations: {
        setRenameConversationName(state, name) {
            state.renameConversationName = name;
        },

        setUserInput(state, userInput) {
            state.userInput = userInput;
        },

        setRespondingMessageIndex(state) {
            state.respondingMessageIndex = null;
        },

        setConversationContainerElement(state, element) {
            state.conversationContainerElement = element;
        },

        setUserInputElement(state, element) {
            state.userInputElement = element;
        },

        setAssistantTyping(state, assistantTyping) {
            state.isAssistantTyping = assistantTyping;
        },

        setAssistantResponseShown(state, assistantResponseShown) {
            state.assistantResponseShown = assistantResponseShown;
        },

        setRegeneratedConversationId(state, id) {
            state.regeneratedConversationId = id;
        },

        setContext(state, context) {
            state.context = context;
        },

        setPopupType(state, popupType) {
            state.popupType = popupType;
        },
    },

    actions: {
        async loadConversations({ state }) {
            state.isLoadingConversations = true;

            try {
                const response = await WoliService.getConversations();
                const result = response.data.data;

                state.conversations = result.conversations.map((conversation) => {
                    return {
                        id: conversation.id,
                        name: conversation.name,
                        latestResponseGeneratedAt: moment(conversation.latestResponseGeneratedAt),
                    };
                });
            }
            catch (error) {
                helpers.showFailNotification('Failed to get conversations.', {
                    error,
                });
            }
            finally {
                state.isLoadingConversations = false;
            }
        },

        async loadConversation({ state, dispatch }, conversationId) {
            dispatch('cancelLoadConversation');

            state.isLoadingConversation = true;
            state.conversationId = conversationId;
            state.limitReachedMessage = null;

            try {
                const response = await WoliService.getConversation(conversationId, state.loadConversationAbortController.signal);
                const result = response.data.data;

                state.messages = result.messages;
                state.assistantResponseShown = result.assistantResponseShown;

                setTimeout(() => {
                    dispatch('scrollToConversationEnd');
                });
            }
            catch (error) {
                if(!api.isCanceled(error)) {
                    helpers.showFailNotification('Failed to get messages.', {
                        error,
                    });

                    state.conversationId = null;
                }
            }
            finally {
                state.isLoadingConversation = false;
            }
        },

        async deleteConversation({ state, dispatch }, conversationId) {
            const currentConversationId = state.conversationId;
            if(currentConversationId === conversationId) {
                state.conversationId = null;
            }

            try {
                await WoliService.deleteConversation(conversationId);
                if(currentConversationId === conversationId) {
                    dispatch('startNewChat');
                }

                await dispatch('loadConversations');
            }
            catch (error) {
                state.conversationId = currentConversationId;
    
                helpers.showFailNotification('Failed to delete conversation.', {
                    error,
                });
            }
        },

        async deleteAllConversations({ state, dispatch }) {
            state.isLoadingConversations = true;

            const conversationId = state.conversationId;
            state.conversationId = null;

            try {
                await WoliService.deleteAllConversations();
                dispatch('startNewChat');
            }
            catch (error) {
                state.conversationId = conversationId;

                helpers.showFailNotification('Failed to delete all conversations.', {
                    error,
                });
            }
            finally {
                await dispatch('loadConversations');
            }
        },

        async startConversation({ state, getters, dispatch }) {
            const userInput = state.userInput.trim();
            state.userInput = null;

            const contextType = state.context.type;
            state.context.type = null;

            const contextVariables = state.context.variables;
            state.context.variables = {};

            state.pendingMessage = { content: userInput };
            state.isAssistantResponding = true;

            try {
                const response = await WoliService.createConversation(userInput, contextType, contextVariables, getters.accountId);
                const result = response.data.data;

                state.limitReachedMessage = null;
                const conversationId = result.id;

                state.messages = result.messages;

                state.assistantResponseShown = result.assistantResponseShown;
                state.respondingMessageIndex = state.messages.length - 1;
                state.isAssistantTyping = true;
                state.pendingMessage = null;

                setTimeout(() => {
                    dispatch('scrollToConversationEnd');
                });

                await WoliService.generateConversationName(result.id);
                await dispatch('loadConversations');

                state.conversationId = conversationId;
            }
            catch (error) {
                if (error.response && error.response.status === 429) {
                    state.limitReachedMessage = WoliService.getLimitErrorMessage(error.response.data.data[0].errorCode);
                } else {
                    helpers.showFailNotification('Failed to create conversation.', {
                        error,
                    });
                }
            }
            finally {
                state.isAssistantResponding = false;
                state.pendingMessage = null;
            }
        },

        async updateConversation({ state, dispatch }) { 
            const userInput = state.userInput.trim();

            state.userInput = null;
            state.pendingMessage = { content: userInput, id: state.conversationId };
            state.isAssistantResponding = true;

            setTimeout(() => {
                dispatch('scrollToConversationEnd');
            });

            try {
                const response = await WoliService.promptConversation(state.conversationId, userInput, state.assistantResponseShown);
                const result = response.data.data;

                state.limitReachedMessage = null;

                if(state.pendingMessage.id === state.conversationId) {
                    state.messages = result.messages;

                    state.assistantResponseShown = result.assistantResponseShown;
                    state.respondingMessageIndex = state.messages.length - 1;
                    state.isAssistantTyping = true;
                    
                    const conversation = state.conversations.find((c) => c.id === state.pendingMessage.id);
                    if(conversation) {
                        conversation.latestResponseGeneratedAt = moment(result.latestResponseGeneratedAt);
                    }
                }
            }
            catch (error) {
                if(error.response && error.response.status === 429) {
                    state.limitReachedMessage = WoliService.getLimitErrorMessage(error.response.data.data[0].errorCode);
                } else {
                    helpers.showFailNotification('Failed to update conversation.', {
                        error,
                    });
                }
            }
            finally {
                state.pendingMessage = null;
                state.isAssistantResponding = false;
            }
        },

        async updateAssistantResponseShown({ state }) {
            await WoliService.updateAssistantResponseShown(state.conversationId, state.assistantResponseShown);
        },

        async regenerateMessage({ state, dispatch }) {
            state.isAssistantResponding = true;

            setTimeout(() => {
                dispatch('scrollToConversationEnd');
            });

            try {
                const response = await WoliService.regenerateResponse(state.conversationId, state.assistantResponseShown);
                const result = response.data.data;

                state.limitReachedMessage = null;

                if(state.regeneratedConversationId === state.conversationId) {
                    state.messages = result.messages;

                    state.assistantResponseShown = result.assistantResponseShown;
                    state.respondingMessageIndex = state.messages.length - 1;
                    state.isAssistantTyping = true;

                    const conversation = state.conversations.find((c) => c.id === state.regeneratedConversationId);
                    if (conversation) {
                        conversation.latestResponseGeneratedAt = moment(result.latestResponseGeneratedAt);
                    }
                }
            }
            catch (error) {
                if (error.response.status === 429) {
                    state.limitReachedMessage = WoliService.getLimitErrorMessage(error.response.data.data[0].errorCode);
                } else {
                    helpers.showFailNotification('Failed to regenerate message.', {
                        error,
                    });
                }
            }
            finally {
                state.isAssistantResponding = false;
            }
        },

        startNewChat({ state, dispatch }) {
            dispatch('cancelLoadConversation');

            state.conversationId = null;
            state.messages = null;
            state.limitReachedMessage = null;

            dispatch('focusUserInput');
        },

        beginRenameConversation({ state }, { conversationId, conversationName }) {
            state.renameConversationId = conversationId;
            state.renameConversationName = conversationName;
        },

        cancelRenameConversation({ state }) {
            state.renameConversationId = null;
            state.renameConversationName = null;
        },

        async confirmRenameConversation({ state, getters }) {
            const conversation = getters.renamingConversation;
            const newName = state.renameConversationName;

            if(!conversation || !newName) {
                return Promise.resolve();
            }

            const oldName = conversation.name;

            if(newName === oldName) {
                state.renameConversationId = null;
                return Promise.resolve();
            }

            state.isLoadingRename = true;
            conversation.name = newName;

            try {
                const response = await WoliService.renameConversation(conversation.id, newName);
                const result = response.data.data;

                conversation.name = result.name;
            }
            catch (error) {
                helpers.showFailNotification('Failed to rename conversation.', {
                    error,
                });

                conversation.name = oldName;
            }
            finally {
                state.isLoadingRename = false;
                state.renameConversationId = null;
            }
        },

        scrollToConversationEnd({ state }) {
            if(!state.conversationContainerElement) {
                return;
            }

            state.conversationContainerElement.scrollTo({
                top: state.conversationContainerElement.scrollHeight,
                behavior: 'smooth',
            });
        },

        focusUserInput({ state }) {
            if(!state.userInputElement) {
                return;
            }

            setTimeout(() => {
                state.userInputElement.focus();
            });
        },

        cancelLoadConversation({ state }) {
            state.loadConversationAbortController.abort();
            state.loadConversationAbortController = new AbortController();
        }
    },
});